import {drawStudentReceipt} from '../utils/studentReceipt';
import { studentFeeMonth } from '../utils/studentFeeMonths';


import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';

// Point axios to API (keeps working even if you use a dev proxy later)
if (!axios.defaults.baseURL) {
  axios.defaults.baseURL = 'http://localhost:5050';
}


function toYMD(d) {
  if (!d) return '';
  const dt = new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Format YYYY-MM-DD in a specific timezone (default: IST)
function toYMDInTZ(d, tz = 'Asia/Kolkata') {
  if (!d) return '';
  const dtf = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = dtf.formatToParts(new Date(d));
  const y = parts.find(p => p.type === 'year')?.value || '';
  const m = parts.find(p => p.type === 'month')?.value || '';
  const day = parts.find(p => p.type === 'day')?.value || '';
  return `${y}-${m}-${day}`;
}

function normalizeFeeLinesForPut(feeLines = []) {
  return (feeLines || []).map(fl => {
    const out = {
      type: fl.type,
      amount: Number(fl.amount || 0)
    };
    if (fl.note) out.note = fl.note;
    if (fl.eventId) out.eventId = typeof fl.eventId === 'object' && fl.eventId._id ? fl.eventId._id : fl.eventId;
    if (fl.courseId) out.courseId = typeof fl.courseId === 'object' && fl.courseId._id ? fl.courseId._id : fl.courseId;
    return out;
  });
}
function downloadReceiptForPayment(p,studentMap){
  const sid=typeof p.studentId==='object'?p.studentId?._id:p.studentId;
  const student=studentMap.get(String(sid))||(typeof p.studentId==='object'?p.studentId:{});
  const doc=new jsPDF({unit:'pt',format:'a4'});
  const info=drawStudentReceipt(doc,p,student||{});
  doc.save(`receipt_${info.organization.replace(/\s+/g,'')}_${info.studentCode==='Not recorded'?sid:info.studentCode}_${info.day}.pdf`);
}
function summarizeFeeTypes(payment) {
  if (!payment?.feeLines?.length) return '-';
  const parts = [];
  let late = 0;
  for (const fl of payment.feeLines) {
    switch (fl.type) {
      case 'admissionFee': parts.push('Admission'); break;
      case 'monthlyFee': parts.push(`Monthly (${studentFeeMonth(payment) || 'month unavailable'})`); break;
      case 'eventFee': parts.push(fl?.eventId?.name ? `Event (${fl.eventId.name})` : 'Event'); break;
      case 'oneTimeFee':
      case 'onetimeFee':
      case 'onetimeCourseFee': parts.push('One‑time Course'); break;
      case 'miscFee': parts.push(fl.note ? `Misc (${fl.note})` : 'Misc'); break;
      case 'lateFee': late += Number(fl.amount || 0); break;
      default: break;
    }
  }
  const uniq = [...new Set(parts)];
  if (late > 0) uniq.push(`+ Late ₹${late}`);
  return uniq.join(' + ');
}

export default function PaymentEdit() {
  const [payments, setPayments] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [org, setOrg] = useState('All');

  // Editing modal
  const [editing, setEditing] = useState(null); // payment object
  const [draft, setDraft] = useState(null);

  // pagination (client-side)
  const [page, setPage] = useState(1);
  const pageSize = 15;

  useEffect(() => {
    let cancel = false;
    async function load() {
      try {
        setLoading(true); setError('');
        const [payRes, stuRes] = await Promise.all([
          axios.get('/api/payments'),
          axios.get('/api/students')
        ]);
        if (cancel) return;
        setPayments(Array.isArray(payRes.data) ? payRes.data : payRes.data?.payments || []);
        setStudents(Array.isArray(stuRes.data) ? stuRes.data : []);
      } catch (e) {
        console.error('Failed to load payments/students', e);
        setError('Failed to load data');
      } finally {
        if (!cancel) setLoading(false);
      }
    }
    load();
    return () => { cancel = true; };
  }, []);

  const studentMap = useMemo(() => {
    const m = new Map();
    for (const s of students) m.set(String(s._id), s);
    return m;
  }, [students]);

  const orgOptions = useMemo(() => {
    const set = new Set(['All']);
    for (const s of students) {
      const o = s?.course?.organization;
      if (o) set.add(o);
    }
    return Array.from(set);
  }, [students]);

  const filtered = useMemo(() => {
    return payments.filter(p => {
      // date range
      if (from) {
        const d = new Date(p.date);
        if (d < new Date(from)) return false;
      }
      if (to) {
        const d = new Date(p.date);
        if (d > new Date(to)) return false;
      }
      // org
      if (org !== 'All') {
        const sid = typeof p.studentId === 'object' ? p.studentId?._id : p.studentId;
        const s = studentMap.get(String(sid));
        const o = s?.course?.organization;
        if (o !== org) return false;
      }
      // text search in student name/id
      if (q.trim()) {
        const sid = typeof p.studentId === 'object' ? p.studentId?._id : p.studentId;
        const s = studentMap.get(String(sid));
        const name = (typeof p.studentId === 'object' ? p.studentId?.name : s?.name) || '';
        const code = (typeof p.studentId === 'object' ? p.studentId?.studentId : s?.studentId) || '';
        const hay = `${name} ${code}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [payments, from, to, org, q, studentMap]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page]);

  function openEdit(p) {
    setEditing(p);
    setDraft({
      date: toYMDInTZ(p.date),
      feeMonth: studentFeeMonth(p),
      paymentMode: p.paymentMode || 'UPI',
      discount: Number(p.discount || 0),
      feeLines: (p.feeLines || []).map(fl => ({
        type: fl.type,
        amount: Number(fl.amount || 0),
        note: fl.note || '',
        courseId: fl.courseId || '',
        eventId: fl.eventId?._id || fl.eventId || '',
      }))
    });
  }

  function closeEdit() {
    setEditing(null); setDraft(null);
  }

// Helper for admin confirmation and password
function confirmAdmin(actionLabel = 'this action') {
  // Step 1: user intent check
  const ok = window.confirm(`Are you sure you want to ${actionLabel}?`);
  if (!ok) return false;
  // Step 2: admin password (not logged anywhere)
  const pw = window.prompt('Enter admin password to continue');
  if (pw !== 'admin') {
    alert('Unauthorized');
    return false;
  }
  return true;
}

const saveEdit = async () => {
  if (!editing || !draft) return;
  if (!confirmAdmin('save changes to this payment')) return;
  try {
    // student id is not editable here; take from original doc
    const sid = typeof editing.studentId === 'object'
      ? editing.studentId._id
      : editing.studentId;

    // normalize lines from the draft the user just edited
    const normFeeLines = normalizeFeeLinesForPut(draft.feeLines || []);
    const discountNum = Number(draft.discount || 0);

    const gross = normFeeLines.reduce((a, fl) => a + Number(fl.amount || 0), 0);
    const totalAmount = Math.max(gross - discountNum, 0);

    // use draft date + payment mode
    const payload = {
      studentId: sid,
      ...((draft.date || editing.date) ? { date: draft.date && draft.date !== toYMDInTZ(editing.date) ? draft.date : editing.date } : {}),
      ...(draft.feeLines.some(l=>l.type==='monthlyFee') ? { feeMonth: draft.feeMonth } : {}),                 // keep as yyyy-mm-dd string
      paymentMode: draft.paymentMode || 'UPI',
      discount: discountNum,
      feeLines: normFeeLines,
      totalAmount
    };

    const res = await axios.put(`/api/payments/${editing._id}`, { ...payload, expectedVersion: editing.__v || 0 });
    // backend may return either the updated doc or { message, payment }
    const updated = res?.data?.payment || res?.data;

    // update local list optimistically
    setPayments(prev =>
      prev.map(p => (p._id === editing._id ? { ...p, ...(updated || payload) } : p))
    );

    closeEdit();
  } catch (e) {
    const serverMsg = e?.response?.data?.error || e?.response?.data?.message;
    console.error('Update failed', serverMsg || e.message, e?.response?.data);
    alert(`Update failed: ${serverMsg || e.message}`);
  }
};

  async function deletePayment(id) {
    const payment = payments.find(row => row._id === id);
    const eventPayment = (payment?.feeLines || []).some(line => line.type === 'eventFee');
    let data;
    if (eventPayment) {
      const reason = window.prompt('Why is this event payment being removed? This is a correction, not a refund.');
      if (reason == null) return;
      if (!reason.trim()) { alert('A correction reason is required.'); return; }
      if (!window.confirm(`Remove receipt ${id} from collections and reset its event payment amount? The original receipt will remain in audit history. Participation will remain unchanged.`)) return;
      data = { expectedVersion: payment.__v || 0, confirm: true, reason: reason.trim() };
    } else {
      if (!window.confirm('Delete this payment permanently?')) return;
      if (window.prompt('Enter admin password to delete') !== 'admin') { alert('Unauthorized'); return; }
    }
    try {
      await axios.delete(`/api/payments/${id}`, { data });
      setPayments(previous => previous.filter(payment => payment._id !== id));
      if (eventPayment) alert('Mistaken payment removed. You can now enter the corrected payment.');
    } catch (error) { alert(error.response?.data?.error || 'Unable to remove payment. Reload before retrying.'); }
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <h2 className="text-2xl font-semibold mb-4">Payments — Manage</h2>

      {/* Filters */}
      <div className="bg-white rounded shadow p-4 mb-4 flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-sm text-gray-600 mb-1">Search Student</label>
          <input value={q} onChange={e => { setQ(e.target.value); setPage(1); }} className="border rounded px-3 py-2" placeholder="Name or Code" />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1">From</label>
          <input type="date" value={from} onChange={e => { setFrom(e.target.value); setPage(1); }} className="border rounded px-3 py-2" />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1">To</label>
          <input type="date" value={to} onChange={e => { setTo(e.target.value); setPage(1); }} className="border rounded px-3 py-2" />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1">Organization</label>
          <select value={org} onChange={e => { setOrg(e.target.value); setPage(1); }} className="border rounded px-3 py-2">
            {orgOptions.map(o => (<option key={o} value={o}>{o}</option>))}
          </select>
        </div>
        <div className="ml-auto text-sm text-gray-500">{filtered.length} result(s)</div>
      </div>

      {loading && <div>Loading…</div>}
      {error && <div className="text-red-600">{error}</div>}

      {!loading && !error && (
        <div className="bg-white rounded shadow overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-2">Date</th>
                <th className="text-left px-4 py-2">Student</th>
                <th className="text-left px-4 py-2">Types</th>
                <th className="text-left px-4 py-2">Mode</th>
                <th className="text-right px-4 py-2">Total (₹)</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paged.map(p => {
                const sid = typeof p.studentId === 'object' ? p.studentId?._id : p.studentId;
                const s = studentMap.get(String(sid));
                const name = (typeof p.studentId === 'object' ? p.studentId?.name : s?.name) || '-';
                const code = (typeof p.studentId === 'object' ? p.studentId?.studentId : s?.studentId) || '';
                const label = code ? `${name} (${code})` : name;
                return (
                  <tr key={p._id} className="border-t">
                    <td className="px-4 py-2 whitespace-nowrap">{toYMD(p.date)}</td>
                    <td className="px-4 py-2">{label}</td>
                    <td className="px-4 py-2">{summarizeFeeTypes(p)}</td>
                    <td className="px-4 py-2">{p.paymentMode || 'UPI'}</td>
                    <td className="px-4 py-2 text-right">{Number(p.totalAmount || 0).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex justify-end items-center gap-4">
                        <button onClick={() => openEdit(p)} className="inline-flex items-center text-indigo-600 hover:underline">Edit</button>
                        <button
                          type="button"
                          onClick={() => downloadReceiptForPayment(p, studentMap)}
                          className="text-rose-600 hover:underline"
                          title="Download receipt PDF"
                        >
                          Receipt
                        </button>
                        <button onClick={() => deletePayment(p._id)} className="inline-flex items-center text-red-600 hover:underline">Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {paged.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-gray-500">No payments match your filters.</td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {filtered.length > pageSize && (
            <div className="flex items-center justify-between px-4 py-3 border-t text-sm">
              <div>Page {page} of {Math.ceil(filtered.length / pageSize)}</div>
              <div className="space-x-2">
                <button disabled={page===1} onClick={() => setPage(p => Math.max(1, p-1))} className={`px-3 py-1 border rounded ${page===1 ? 'opacity-50 cursor-not-allowed' : ''}`}>Prev</button>
                <button disabled={page*pageSize>=filtered.length} onClick={() => setPage(p => p+1)} className={`px-3 py-1 border rounded ${(page*pageSize)>=filtered.length ? 'opacity-50 cursor-not-allowed' : ''}`}>Next</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Edit Modal */}
      {editing && draft && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-3xl rounded shadow-lg overflow-hidden">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <div className="font-semibold">Edit Payment</div>
              <button onClick={closeEdit} className="text-gray-500 hover:text-black">✕</button>
            </div>

            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Date</label>
                  <input type="date" value={draft.date} onChange={e => setDraft(d => ({...d, date: e.target.value}))} className="border rounded px-3 py-2 w-full" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Payment Mode</label>
                  <select value={draft.paymentMode} onChange={e => setDraft(d => ({...d, paymentMode: e.target.value}))} className="border rounded px-3 py-2 w-full">
                    <option>UPI</option>
                    <option>Cash</option>
                    <option>Card</option>
                    <option>NetBanking</option>
                    <option>Others</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Discount (₹)</label>
                  <input type="number" min={0} value={draft.discount} onChange={e => setDraft(d => ({...d, discount: Number(e.target.value||0)}))} className="border rounded px-3 py-2 w-full" />
                </div>
              </div>

              {draft.feeLines.some(line=>line.type==='monthlyFee') && <label className="block text-sm">Fee Month<input aria-label="Fee Month" type="month" required value={draft.feeMonth || ''} onChange={event=>setDraft(previous=>({...previous,feeMonth:event.target.value}))} className="block border rounded px-3 py-2" /></label>}
              <div>
                <div className="font-medium mb-2">Fee Lines</div>
                <div className="space-y-3">
                  {draft.feeLines.map((fl, idx) => (
                    <div key={idx} className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end bg-gray-50 p-3 rounded">
                      <div className="md:col-span-2">
                        <label className="block text-sm text-gray-600 mb-1">Type</label>
                        <input value={fl.type} readOnly className="border rounded px-3 py-2 w-full bg-gray-100" />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Amount (₹)</label>
                        <input type="number" min={0} value={fl.amount} onChange={e => {
                          const v = Number(e.target.value||0);
                          setDraft(d => ({...d, feeLines: d.feeLines.map((x,i)=> i===idx?{...x, amount:v}:x)}));
                        }} className="border rounded px-3 py-2 w-full" />
                      </div>
                      <div className="md:col-span-3">
                        <label className="block text-sm text-gray-600 mb-1">Note</label>
                        <input value={fl.note || ''} onChange={e => {
                          const v = e.target.value;
                          setDraft(d => ({...d, feeLines: d.feeLines.map((x,i)=> i===idx?{...x, note:v}:x)}));
                        }} className="border rounded px-3 py-2 w-full" placeholder="Optional" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="text-right text-sm text-gray-600">
                <div>
                  Subtotal: ₹{draft.feeLines.reduce((s,fl)=> s + Number(fl.amount||0), 0).toLocaleString('en-IN')}
                </div>
                <div>
                  Grand Total: <span className="font-semibold">₹{Math.max(0, draft.feeLines.reduce((s,fl)=> s + Number(fl.amount||0), 0) - Number(draft.discount||0)).toLocaleString('en-IN')}</span>
                </div>
              </div>
            </div>

            <div className="px-4 py-3 border-t flex items-center justify-end gap-3">
              <button onClick={closeEdit} className="px-4 py-2 border rounded">Cancel</button>
              <button onClick={saveEdit} className="px-4 py-2 bg-indigo-600 text-white rounded">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}